import { ref, computed } from 'vue'

// Source - https://stackoverflow.com/a/51847335
// Posted by kremerd
// Retrieved 2026-02-05, License - CC BY-SA 4.0
/**
 * The BeforeInstallPromptEvent is fired at the Window.onbeforeinstallprompt handler
 * before a user is prompted to "install" a website to a home screen on mobile.
 *
 * @deprecated Only supported on Chrome and Android Webview.
 */
export interface BeforeInstallPromptEvent extends Event {
  /**
   * Returns an array of DOMString items containing the platforms on which the event was dispatched.
   * This is provided for user agents that want to present a choice of versions to the user such as,
   * for example, "web" or "play" which would allow the user to chose between a web version or
   * an Android version.
   */
  readonly platforms: Array<string>

  /**
   * Returns a Promise that resolves to a DOMString containing either "accepted" or "dismissed".
   */
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>

  /**
   * Allows a developer to show the install prompt at a time of their own choosing.
   * This method returns a Promise.
   */
  prompt(): Promise<void>
}

// Module-level singleton state
const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const canInstall = ref(false)
const isInstalled = ref(false)
const isGuideShown = ref(false)

export const isIOSDevice = (
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  platform = typeof navigator !== 'undefined' ? navigator.platform : '',
  maxTouchPoints = typeof navigator !== 'undefined'
    ? (navigator.maxTouchPoints ?? 0)
    : 0
) => {
  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === 'MacIntel' && maxTouchPoints > 1)
  )
}

export const checkIsInstalled = () => {
  if (typeof window === 'undefined') return false

  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)')?.matches ?? false
  const isIOSStandalone = (window.navigator as any)?.standalone === true
  const isCapacitor = (window as any)?.Capacitor?.isNativePlatform?.() ?? false

  return isStandalone || isIOSStandalone || isCapacitor
}

const updateInstalledStatus = () => {
  isInstalled.value = checkIsInstalled()
}

// Init listeners once at module level
if (typeof window !== 'undefined') {
  updateInstalledStatus()

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt.value = e as BeforeInstallPromptEvent
    canInstall.value = true
  })

  window.addEventListener('appinstalled', () => {
    deferredPrompt.value = null
    canInstall.value = false
    isInstalled.value = true
    isGuideShown.value = false
  })

  const mediaQuery = window.matchMedia?.('(display-mode: standalone)')
  mediaQuery?.addEventListener?.('change', updateInstalledStatus)
}

export function usePwaStatus() {
  updateInstalledStatus()
  return { isInstalled }
}

export function usePwaInstall() {
  updateInstalledStatus()

  const isIOS = computed(() => isIOSDevice())

  const promptInstall = async () => {
    if (isIOS.value) {
      isGuideShown.value = true
      return
    }
    if (!deferredPrompt.value) return

    try {
      await deferredPrompt.value.prompt()
      const { outcome } = await deferredPrompt.value.userChoice
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt')
      }
    } catch (err) {
      console.error('Error invoking install prompt:', err)
    } finally {
      deferredPrompt.value = null
      canInstall.value = false
    }
  }

  const installPwa = promptInstall

  const closeGuide = () => {
    isGuideShown.value = false
  }

  const openGuide = () => {
    isGuideShown.value = true
  }

  return {
    canInstall,
    isInstalled,
    isIOS,
    isGuideShown,
    promptInstall,
    installPwa,
    closeGuide,
    openGuide
  }
}
